import { Injectable, inject } from '@angular/core';
import { Observable, timeout } from 'rxjs';
import { ApiClientService } from '../../../core/http/api-client.service';
import {
  CreateClinicRequest,
  ClinicDetailDto,
  ClinicProcedureAssignmentDto,
  ClinicOperationalSettingsDto,
  CompanyMailSettingsDto,
  CompanyOperationsSettingsDto,
  CompanyRatingSettingsDto,
  UpdateClinicRequest,
  SaveClinicSettingsRequest,
  ClinicSaveOutcome,
  UpdateCompanyMailSettingsRequest,
  UpdateCompanyOperationsSettingsRequest,
  UpdateCompanyRatingSettingsRequest,
  SendTestMailRequest,
} from './settings.models';

@Injectable({
  providedIn: 'root'
})
export class SettingsService
{
  private readonly apiClient = inject(ApiClientService);

  public getOperationsSettings(companyId?: string): Observable<CompanyOperationsSettingsDto>
  {
    return this.apiClient.get<CompanyOperationsSettingsDto>(
      '/company-settings/operations',
      companyId ? { companyId } : undefined,
    );
  }

  public updateOperationsSettings(
    payload: UpdateCompanyOperationsSettingsRequest,
    companyId?: string,
  ): Observable<CompanyOperationsSettingsDto>
  {
    return this.apiClient.patch<CompanyOperationsSettingsDto, UpdateCompanyOperationsSettingsRequest>(
      '/company-settings/operations',
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public getMailSettings(companyId?: string): Observable<CompanyMailSettingsDto | null>
  {
    return this.apiClient.get<CompanyMailSettingsDto | null>(
      '/company-settings/admin/mail',
      companyId ? { companyId } : undefined,
    );
  }

  public updateMailSettings(
    payload: UpdateCompanyMailSettingsRequest,
    companyId?: string,
  ): Observable<CompanyMailSettingsDto | null>
  {
    return this.apiClient.patch<CompanyMailSettingsDto | null, UpdateCompanyMailSettingsRequest>(
      '/company-settings/admin/mail',
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public getRatingSettings(companyId?: string): Observable<CompanyRatingSettingsDto>
  {
    return this.apiClient.get<CompanyRatingSettingsDto>(
      '/company-settings/admin/rating',
      companyId ? { companyId } : undefined,
    );
  }

  public updateRatingSettings(
    payload: UpdateCompanyRatingSettingsRequest,
    companyId?: string,
  ): Observable<CompanyRatingSettingsDto>
  {
    return this.apiClient.patch<CompanyRatingSettingsDto, UpdateCompanyRatingSettingsRequest>(
      '/company-settings/admin/rating',
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public sendTestMail(
    payload: SendTestMailRequest,
    companyId?: string,
  ): Observable<{ sent: true }>
  {
    return this.apiClient.post<{ sent: true }, SendTestMailRequest>(
      '/company-settings/admin/mail/test',
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public getClinicOperationalSettings(companyId?: string): Observable<ClinicOperationalSettingsDto[]>
  {
    return this.apiClient.get<ClinicOperationalSettingsDto[]>(
      '/company-settings/clinics',
      companyId ? { companyId } : undefined,
    );
  }

  public getClinicDetail(clinicId: string, companyId?: string): Observable<ClinicDetailDto>
  {
    return this.apiClient.get<ClinicDetailDto>(
      `/clinics/${encodeURIComponent(clinicId)}/detail`,
      companyId ? { companyId } : undefined,
    );
  }

  public saveClinicSettings(
    clinicId: string,
    payload: SaveClinicSettingsRequest,
    companyId?: string,
  ): Observable<ClinicSaveOutcome>
  {
    return this.apiClient.post<ClinicSaveOutcome, SaveClinicSettingsRequest>(
      `/company-settings/clinics/${clinicId}/save`,
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(timeout(35_000));
  }

  public getClinicProcedureAssignments(
    clinicId: string,
    companyId?: string,
  ): Observable<ClinicProcedureAssignmentDto[]>
  {
    return this.apiClient.get<ClinicProcedureAssignmentDto[]>(
      `/company-settings/clinics/${clinicId}/procedure-types`,
      companyId ? { companyId } : undefined,
    );
  }

  public getClinicSaveOutcome(
    clinicId: string,
    operationId: string,
    companyId?: string,
  ): Observable<ClinicSaveOutcome>
  {
    return this.apiClient.get<ClinicSaveOutcome>(
      `/company-settings/clinics/${clinicId}/save-operations/${operationId}`,
      companyId ? { companyId } : undefined,
    ).pipe(timeout(35_000));
  }

  public createClinic(payload: CreateClinicRequest, companyId?: string): Observable<ClinicDto>
  {
    return this.apiClient.post<ClinicDto, CreateClinicRequest>(
      '/clinics',
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public updateClinic(
    clinicId: string,
    payload: UpdateClinicRequest,
    companyId?: string,
  ): Observable<ClinicDto>
  {
    return this.apiClient.patch<ClinicDto, UpdateClinicRequest>(
      `/clinics/${clinicId}`,
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public deleteClinic(clinicId: string, companyId?: string): Observable<{ deleted: true }>
  {
    return this.apiClient.delete<{ deleted: true }>(
      `/clinics/${clinicId}`,
      companyId ? { companyId } : undefined,
    );
  }
}

interface ClinicDto {
  id: string;
  companyId: string;
  name: string;
  city: string | null;
  address: string | null;
  createdAt: string;
  updatedAt: string;
}
